import * as React from 'react'
import {
  AppBar,
  Box,
  Button,
  Divider,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
} from '@mui/material'
import WotcApplicantService from '../service/wotcapplicant'

const style = {
  bgcolor: 'background.paper',
  boxShadow: 24,
  left: '50%',
  p: 4,
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 1336,
}

const thStyles = {
  '&::after': {
    borderLeft: '2px #e0e0e0 solid',
    content: '""',
    height: '30%',
    marginBottom: 'auto',
    marginTop: 'auto',
    position: 'absolute',
    right: 0,
    top: '35%',
  },
  position: 'relative',
}

type HistoryData = {
	id: number
	date: string
	user: string
	value: string
	change: string
}
type Props = {
	clientId: string
}

type ChangeDetail = {
  currentValue: string,
  previousValue: string,
  propertyName: string,
  action: string,
  type: string,
  keys: Key[]
}

type Key = {
  property: string,
  value: string
}

export default function ConfigureWotcEditHistory({ clientId }: Props) {
  const [open, setOpen] = React.useState(false)
  const [isPageLoading, setIsPageLoading] = React.useState(false)

  const handleClose = () => setOpen(false)
  const [pg, setPg] = React.useState(0)
  const [rpg, setRpg] = React.useState(10)

  const [pageHistoryData, setPageHistoryData] = React.useState<HistoryData[]>(
    [],
  )

  const handleOpen = () => {
    setOpen(true)
    getWotcEditHistory()
  }
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPg(newPage)    
  }
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRpg(parseInt(event.target.value, 10))
    setPg(0)
  }
  async function getWotcEditHistory() {
    try {
      setIsPageLoading(true)
  
      setPageHistoryData([])
      
      const result = await WotcApplicantService.getWotcEditHistory(clientId)
      let i = 0
      const promises = result.records.flatMap(async (record: { changes: ChangeDetail[]; modifiedDate: string; modifiedBy: string }) => {
        const changePromises = record.changes.map(async (change) => {
          const description = await getChangedVerbose(change)
          return {
            change: description,
            date: getMDTDate(record.modifiedDate),
            id: i++,
            user: record.modifiedBy,
            value: getPropertyValue(change.propertyName)
          }
        })
        return Promise.all(changePromises)
      })

      const historyData = await Promise.all(promises)
      setPageHistoryData(historyData.flat())
      
    } catch (error) {
      // Handle errors here
      console.error('Error fetching Wotc edit history:', error)
    } finally {
      setIsPageLoading(false)
    }
  }

  const getMDTDate = (dateStr: string) => {
    
    const utcDate = new Date(dateStr)

    const mstDate = new Date(utcDate.getTime() + (5 * 60 * 60 * 1000) + (30 * 60 * 1000))

    const formattedDate = new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      month: 'short',         
      second: 'numeric',
      timeZone: 'America/Denver',
      timeZoneName: 'short',
      year: 'numeric',
    })

    const mdtDate = formattedDate.format(mstDate).replace('MST', 'MDT')

    return mdtDate.replace(/,(\s\d{1,2}:)/, '$1')

  }

  const getChangedVerbose = async (change: ChangeDetail) => {
    let description: string

    switch (change.type) {
    case 'Boolean':
      description = (change.currentValue === 'true') ? 'Enabled' : 'Disabled'
      break
    default:
      description = `${change.previousValue || 'Empty'} to ${change.currentValue || 'Empty'}`
      break
    }

    const locationKey = change.keys.find(value => value.property === 'location')
    
    if (locationKey && locationKey.value) {

      const locationResult = await WotcApplicantService.getLocationsByLocationId(clientId, locationKey.value)
      
      if (locationResult &&  locationResult.name) {
        description += ' for '+ locationResult.name
      } else {
        description += ' for closed location'
      }         
      

    }

    return description
    
  }

  const getPropertyValue = (propertyName: string) => {
    const propertyMappings: { [key: string]: string } = {
      'active': 'WOTC',
      'organizationCode': 'Organization Code',
      'providerLocationId': 'Provider Location',
      'providerName': 'Client Vendor'
      // Add more mappings as needed
    }

    return propertyMappings[propertyName] || ''
  }

  return (
    <div>
      <Button onClick={handleOpen} sx={{ fontWeight:400, padding: 0, textTransform: 'none'}}>
				View edit history
      </Button>
      <Modal
        aria-describedby="modal-modal-description"
        aria-labelledby="modal-modal-title"
        disableScrollLock={false}
        onClose={handleClose}
        open={open}
      >
        <Box sx={style}>
          <AppBar component="nav" sx={{ background: '#02122c' }}>
            <Toolbar>
              <b>Edit History</b>
            </Toolbar>
          </AppBar>
          <Box component="main" sx={{ p: 7 }}>
            <TableContainer sx={{ maxHeight: 250 }}>
              <Table aria-label="simple table" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="left" sx={thStyles} width ="214px" >Date</TableCell>
                    <TableCell align="left" sx={thStyles}>User</TableCell>
                    <TableCell align="left" sx={thStyles} width ="214px">Value</TableCell>
                    <TableCell align="left" >Change</TableCell>
                  </TableRow>
                </TableHead>
                
                {pageHistoryData.length > 0 || isPageLoading? 
                  <TableBody>
                    {pageHistoryData.slice(pg * rpg, pg * rpg + rpg).map(row => (
                      <TableRow
                        key={row.id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >                      
                        <TableCell align="left" width ="214px">
                          {row.date}
                        </TableCell>
                        <TableCell align="left">{row.user}</TableCell>
                        <TableCell align="left" width ="214px">{row.value}</TableCell>
                        <TableCell align="left">{row.change}</TableCell>

                      </TableRow>
                    ))}
                  </TableBody> : 'Not Available'}
                
              </Table>
            </TableContainer>
            <Divider />

            <TablePagination
              component="div"
              count={pageHistoryData.length}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              page={pg} 
              rowsPerPage={rpg} 
            />
          </Box>
          <Box></Box>
          <Box>
            <Button
              onClick={() => setOpen(false)}
              sx={{ bottom: 24, position: 'absolute', right: 24 }}
            >
							Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  )
}
